import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import bakeonatorLogo from '../assets/images/logos/bakeonator_logo.png';
import { fetchPublicTiers, submitDeveloperSignup, resendEmailVerification, confirmEmailVerification } from '../services/signupApi';
import { useAuth } from '../context/AuthContext';
import 'bootstrap/dist/css/bootstrap.min.css';

const STEPS = ['tier', 'account', 'partner', 'review', 'verify', 'welcome'];

const AGREEMENT_VERSIONS = {
  terms_version: '2026-01',
  privacy_version: '2026-01',
};

function slugify(value) {
  return (value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 220);
}

function SignupPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const stepParam = searchParams.get('step') || 'tier';
  const step = STEPS.includes(stepParam) ? stepParam : 'tier';
  const navigate = useNavigate();
  const { refreshSession, setIsAuthenticated } = useAuth();

  const [tiers, setTiers] = useState([]);
  const [loadingTiers, setLoadingTiers] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [signupResult, setSignupResult] = useState(null);

  const [form, setForm] = useState({
    tier: '',
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    password_confirm: '',
    partner_name: '',
    partner_slug: '',
    partner_type: 'studio',
    agreements: false,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchPublicTiers();
        if (!cancelled) setTiers(data.tiers || []);
      } catch (e) {
        if (!cancelled) setError('Unable to load tier catalog.');
      } finally {
        if (!cancelled) setLoadingTiers(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const token = searchParams.get('token');
    if (step === 'verify' && token) {
      (async () => {
        try {
          await confirmEmailVerification(token);
          await refreshSession();
          setIsAuthenticated(true);
          setSearchParams({ step: 'welcome' });
        } catch (e) {
          setError(e.message || 'Verification failed.');
        }
      })();
    }
  }, [step, searchParams, refreshSession, setIsAuthenticated, setSearchParams]);

  const selectedTier = useMemo(
    () => tiers.find((t) => t.code === form.tier),
    [tiers, form.tier]
  );

  const goTo = (next) => setSearchParams({ step: next });

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const handlePartnerName = (name) => {
    update({
      partner_name: name,
      partner_slug: form.partner_slug || slugify(name),
    });
  };

  const handleSubmit = async () => {
    setError('');
    setSubmitting(true);
    try {
      const result = await submitDeveloperSignup({
        user: {
          email: form.email,
          password: form.password,
          password_confirm: form.password_confirm,
          first_name: form.first_name,
          last_name: form.last_name,
        },
        partner: {
          name: form.partner_name,
          slug: form.partner_slug,
          type: form.partner_type,
        },
        tier: form.tier,
        agreements: {
          ...AGREEMENT_VERSIONS,
          accepted: form.agreements,
        },
      });
      setSignupResult(result);
      await refreshSession();
      setIsAuthenticated(true);
      goTo('verify');
    } catch (e) {
      setError(e.message || 'Signup failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    setError('');
    try {
      const res = await resendEmailVerification();
      setSignupResult((prev) => ({
        ...prev,
        verification: { ...prev.verification, delivery_status: res.delivery_status },
      }));
    } catch (e) {
      setError(e.message || 'Unable to resend verification email.');
    }
  };

  return (
    <div className="container py-5" style={{ maxWidth: 900 }}>
      <div className="text-center mb-4">
        <img src={bakeonatorLogo} alt="Bakeonator" height={72} className="mb-2" />
        <h1 className="h3">Developer Signup</h1>
        <p className="text-muted">Create your account and Partner workspace in one flow.</p>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {step === 'tier' && (
        <section>
          <h2 className="h5 mb-3">1. Choose your Tools &amp; API Tier</h2>
          {loadingTiers ? (
            <p>Loading tiers…</p>
          ) : (
            <div className="row g-3">
              {tiers.map((tier) => (
                <div className="col-md-4" key={tier.code}>
                  <div
                    className={`card h-100 ${form.tier === tier.code ? 'border-primary' : ''}`}
                    role="button"
                    onClick={() => update({ tier: tier.code })}
                  >
                    <div className="card-body">
                      <h3 className="h6">{tier.display_name}</h3>
                      <p className="small text-muted">{tier.short_description}</p>
                      <p className="fw-semibold mb-1">{tier.display_price}</p>
                      <p className="small">{tier.price_note}</p>
                      {tier.approval_required && (
                        <span className="badge bg-warning text-dark">Approval required</span>
                      )}
                      {tier.payment_required && (
                        <span className="badge bg-secondary ms-1">Checkout later</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-4 d-flex justify-content-between">
            <Link to="/login" className="btn btn-link">Already have an account?</Link>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!form.tier}
              onClick={() => goTo('account')}
            >
              Continue
            </button>
          </div>
        </section>
      )}

      {step === 'account' && (
        <section>
          <h2 className="h5 mb-3">2. Create your account</h2>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label" htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                className="form-control"
                autoComplete="email"
                value={form.email}
                onChange={(e) => update({ email: e.target.value })}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label" htmlFor="first_name">First name</label>
              <input
                id="first_name"
                className="form-control"
                value={form.first_name}
                onChange={(e) => update({ first_name: e.target.value })}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label" htmlFor="last_name">Last name</label>
              <input
                id="last_name"
                className="form-control"
                value={form.last_name}
                onChange={(e) => update({ last_name: e.target.value })}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label" htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                className="form-control"
                autoComplete="new-password"
                value={form.password}
                onChange={(e) => update({ password: e.target.value })}
              />
              <div className="form-text">Use at least 8 characters with letters and numbers.</div>
            </div>
            <div className="col-md-6">
              <label className="form-label" htmlFor="password_confirm">Confirm password</label>
              <input
                id="password_confirm"
                type="password"
                className="form-control"
                autoComplete="new-password"
                value={form.password_confirm}
                onChange={(e) => update({ password_confirm: e.target.value })}
              />
            </div>
          </div>
          <div className="mt-4 d-flex justify-content-between">
            <button type="button" className="btn btn-outline-secondary" onClick={() => goTo('tier')}>Back</button>
            <button type="button" className="btn btn-primary" onClick={() => goTo('partner')}>Continue</button>
          </div>
        </section>
      )}

      {step === 'partner' && (
        <section>
          <h2 className="h5 mb-3">3. Create your Partner workspace</h2>
          <p className="small text-muted">You will be the Partner Owner for this workspace.</p>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label" htmlFor="partner_name">Partner name</label>
              <input
                id="partner_name"
                className="form-control"
                value={form.partner_name}
                onChange={(e) => handlePartnerName(e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label" htmlFor="partner_slug">Slug</label>
              <input
                id="partner_slug"
                className="form-control"
                value={form.partner_slug}
                onChange={(e) => update({ partner_slug: slugify(e.target.value) })}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label" htmlFor="partner_type">Partner type</label>
              <select
                id="partner_type"
                className="form-select"
                value={form.partner_type}
                onChange={(e) => update({ partner_type: e.target.value })}
              >
                <option value="studio">Studio</option>
                <option value="agency">Agency</option>
                <option value="sandbox">Sandbox</option>
              </select>
            </div>
            <div className="col-12">
              <div className="card bg-light">
                <div className="card-body small">
                  <strong>Selected tier:</strong> {selectedTier?.display_name || form.tier}
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 d-flex justify-content-between">
            <button type="button" className="btn btn-outline-secondary" onClick={() => goTo('account')}>Back</button>
            <button type="button" className="btn btn-primary" onClick={() => goTo('review')}>Continue</button>
          </div>
        </section>
      )}

      {step === 'review' && (
        <section>
          <h2 className="h5 mb-3">4. Review and agreements</h2>
          <ul className="list-group mb-3">
            <li className="list-group-item">Email: {form.email}</li>
            <li className="list-group-item">Partner: {form.partner_name} ({form.partner_slug})</li>
            <li className="list-group-item">Tier: {selectedTier?.display_name || form.tier}</li>
            <li className="list-group-item">Billing: No payment is collected in this release.</li>
          </ul>
          <div className="form-check mb-3">
            <input
              id="agreements"
              type="checkbox"
              className="form-check-input"
              checked={form.agreements}
              onChange={(e) => update({ agreements: e.target.checked })}
            />
            <label className="form-check-label" htmlFor="agreements">
              I accept the Terms of Service and Privacy Policy (version 2026-01).
            </label>
          </div>
          <div className="d-flex justify-content-between">
            <button type="button" className="btn btn-outline-secondary" onClick={() => goTo('partner')}>Back</button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!form.agreements || submitting}
              onClick={handleSubmit}
            >
              {submitting ? 'Creating…' : 'Create account'}
            </button>
          </div>
        </section>
      )}

      {step === 'verify' && (
        <section>
          <h2 className="h5 mb-3">5. Verify your email</h2>
          <p>
            We {signupResult?.verification?.delivery_status === 'failed' ? 'could not send' : 'sent'} a
            verification email to <strong>{signupResult?.user?.email || form.email}</strong>.
          </p>
          <p className="small text-muted">
            Until your email is verified, project creation, API keys, invitations, and job execution
            remain disabled.
          </p>
          <button type="button" className="btn btn-outline-primary me-2" onClick={handleResend}>
            Resend verification email
          </button>
          <Link to="/login" className="btn btn-link">Return to sign in</Link>
        </section>
      )}

      {step === 'welcome' && (
        <section className="text-center">
          <h2 className="h5 mb-3">6. Welcome</h2>
          <p>Your email is verified. Continue to the Partner Console to finish onboarding.</p>
          <button type="button" className="btn btn-primary" onClick={() => navigate('/console')}>
            Open Partner Console
          </button>
        </section>
      )}
    </div>
  );
}

export default SignupPage;
